/**
 * AuthBrandPanel — brand canvas shared by the Login and Change-password
 * surfaces.
 *
 * VideoBrandPanel (the exported panel component) renders the brand's living
 * 3D form: a full-bleed, muted, seamlessly looping brand animation with a
 * poster-first paint (src/assets/login-loop-poster.jpg) that fades the video in
 * once it can play. The clip is same-origin (src/assets/login-loop.mp4,
 * bundled by Vite with a content-hashed filename — cache-proof across
 * rebuilds; no CSP change needed, verified against SecurityConfig).
 *
 * ONE clip, ZERO runtime boundaries — by design, not convenience. An
 * earlier build stacked an intro clip ("leaves materializing") over a
 * separately generated loop and handed off between them at runtime; every
 * blink/cut the owner flagged lived at that boundary, which mismatched in
 * three independent ways across iterations (motion direction, brightness,
 * background geometry). Separately generated clips can always mismatch at
 * a splice, so the boundary was removed outright: the loop's wrap point is
 * dissolved inside the asset itself (its last second cross-dissolves into
 * its first, making the loop point an ordinary mid-motion frame with
 * position AND velocity continuous). The runtime never splices anything —
 * the native `loop` attribute repeats a self-continuous clip forever. Do
 * not reintroduce a second clip or a runtime handoff here.
 *
 * The panel is invariantly dark in BOTH app themes — the video itself is
 * dark graphite, and a dark brand panel beside a light form is the intended
 * look here, not a bug that needs "fixing" by inverting. Because of that,
 * the logo/footer overlay's text tokens are pinned to fixed dark-theme
 * values via a locally-scoped custom-property override on the panel root —
 * BrandMark's wordmark follows those pinned values here while staying
 * theme-reactive for its other callers (AppShell rail, SessionSplash); the
 * glyph itself is the brand-constant logo asset either way.
 *
 * Decorative (aria-hidden); the form panel carries accessible content. A
 * scrim (color-mix off --seedz-ink, the theme-invariant ink constant — never
 * --bg, which is LIGHT in light theme and would gut contrast against the
 * always-dark video) keeps the logo/footer legible over any video frame.
 *
 * Reduced motion and narrow viewports (<880px, the width the panel hides
 * at) skip the <video> element entirely rather than just hiding it — no
 * point downloading a clip behind a panel nobody sees. A load error does
 * the same: the poster silently carries the panel (this is decorative
 * media, not a mutation, so a silent fallback is the correct behaviour,
 * unlike the app's no-silent-failure rule for mutations).
 */
import { useEffect, useState, type CSSProperties } from 'react';

import seedzMark from '../../assets/seedz-mark.png';
import posterSrc from '../../assets/login-loop-poster.jpg';
import loopSrc from '../../assets/login-loop.mp4';

// Imported through Vite so the built URLs are content-fingerprinted —
// a stale cached page can never resolve to outdated video bytes (a real
// failure mode: an open tab across rebuilds kept replaying an old clip).
const POSTER_SRC = posterSrc;
const LOOP_SRC = loopSrc;

// The panel hides below this width; the same threshold gates whether the
// <video> is mounted at all.
const PANEL_QUERY = '(min-width: 880px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';


// The supplied logo asset is trimmed to the mark's own content box, so the
// rendered glyph height IS glyphSize; width follows the asset's natural
// tall/narrow aspect (the real mark's proportions — never stretched).
const MARK_ASPECT = 451 / 890;

/**
 * BrandMark — the corporate S-leaf mark (the supplied logo asset,
 * src/assets/seedz-mark.png) plus an optional wordmark.
 *
 * The glyph is the real corporate artwork rendered directly — fidelity to
 * the supplied asset is the direction, replacing the earlier hand-tuned
 * vector recreation. The PNG is brand-constant (the logo's five hues hold in
 * both themes — the sanctioned token-purity exception for logo colors);
 * only the wordmark text is theme-reactive. The standalone favicon
 * (src/assets/seedz-leaves.svg) embeds a downscaled copy of this same
 * asset, since favicons load outside the page and can't share the import.
 *
 * Pass `label` to expose it as an accessible image (mobile form header);
 * omit it inside decorative containers (brand panel, splash).
 */
export function BrandMark({
  glyphSize = 26,
  wordmark = true,
  label,
}: {
  glyphSize?: number;
  wordmark?: boolean;
  label?: string;
}) {
  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const);

  const wordmarkStyle: CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: Math.round(glyphSize * 0.66),
    letterSpacing: '-0.01em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };

  const glyphWidth = Math.round(glyphSize * MARK_ASPECT);

  return (
    <span
      {...a11y}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--text)' }}
    >
      <img
        src={seedzMark}
        alt=""
        aria-hidden="true"
        width={glyphWidth}
        height={glyphSize}
        draggable={false}
        style={{ display: 'block', flexShrink: 0 }}
      />
      {wordmark && (
        <span style={wordmarkStyle}>
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>seedz</span>
          <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> manage</span>
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// useMediaQuery — a small, defensive matchMedia hook. Falls back to a fixed
// value (no crash, no listener) when matchMedia isn't available (jsdom in
// unit tests doesn't implement it at all), and re-syncs live via the
// 'change' event so the panel reacts if the query flips mid-session (window
// resize, an OS-level reduced-motion toggle).
// ---------------------------------------------------------------------------
function useMediaQuery(query: string, fallback: boolean): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(query);
    } catch {
      return;
    }
    setMatches(mql.matches);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}

// The panel is invariantly dark in both app themes (see file banner) — pin
// the text tokens the wordmark and footer read to the dark theme's fixed
// values instead of letting them follow the active (possibly light) theme.
// Scoped as a custom-property override on the panel root only; BrandMark's
// other callers keep following the active theme. (The mark glyph itself is
// a brand-constant PNG and reads no tokens.)
const DARK_ISLAND_TOKENS = {
  '--text': '#f5f5f5',
  '--text-muted': '#a6a6ab',
  '--text-subtle': '#75757c',
} as CSSProperties;

export function VideoBrandPanel() {
  const panelVisible = useMediaQuery(PANEL_QUERY, true);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY, false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoErrored, setVideoErrored] = useState(false);

  const showVideo = panelVisible && !reducedMotion && !videoErrored;

  useEffect(() => {
    if (!showVideo) {
      // Not mounted (reduced motion / narrow viewport / load error) — reset
      // so a future remount fades in from the poster again.
      setVideoReady(false);
    }
  }, [showVideo]);

  // Stacking plan (the panel root isolates its own context): the video sits
  // in a LOW z band (1) and everything that must paint over it (scrims at
  // 10, brand mark + footer at 11) sits in an explicitly HIGHER band.
  //
  // The poster -> video reveal is opacity-only, so it never animates
  // width/height/transform.
  const videoStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: videoReady ? 1 : 0,
    transition: 'opacity var(--dur-slow) var(--ease-out)',
    zIndex: 1,
  };

  return (
    <div
      aria-hidden="true"
      style={{
        ...DARK_ISLAND_TOKENS,
        display: panelVisible ? 'flex' : 'none',
        width: '60%',
        minWidth: 480,
        flexShrink: 0,
        position: 'relative',
        background: 'var(--seedz-ink)',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '46px 54px',
        overflow: 'hidden',
        isolation: 'isolate',
        boxSizing: 'border-box',
      }}
    >
      {/* Poster — paints immediately; stays mounted underneath the video so
          a load error (or reduced-motion / a narrow viewport) falls back to
          it silently. It is the loop's own first frame, so the fade-in
          reveals motion, not a scene change. */}
      <img
        src={POSTER_SRC}
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* The seamless loop — the panel's only clip. Autoplays muted, repeats
          forever via the native `loop` attribute; the wrap is dissolved
          inside the asset (see file banner), so playback is boundary-free
          by construction. */}
      {showVideo && (
        <video
          aria-hidden="true"
          autoPlay
          muted
          loop
          playsInline
          poster={POSTER_SRC}
          preload="auto"
          src={LOOP_SRC}
          onCanPlay={() => setVideoReady(true)}
          onError={() => setVideoErrored(true)}
          style={videoStyle}
        />
      )}

      {/* Top scrim — legibility for the logo lockup over any video frame.
          z-index 10: above the video (1), below the text overlays (11). */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '34%',
          background:
            'linear-gradient(to bottom, color-mix(in srgb, var(--seedz-ink) 68%, transparent), transparent)',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />

      {/* Bottom scrim — legibility for the footer band. Holds full 88% ink
          strength through the footer's fixed 96px band (46px padding + two
          11px mono lines) before fading, instead of a plain fade that
          dropped to ~51% alpha at the text on short viewports. 88% held
          composites the poster's brightest bottom-band pixel (L 0.465) to a
          6.1:1 ratio under the footer's --text-muted — WCAG 4.5:1 with
          headroom for video frames brighter than the poster. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30%',
          background:
            'linear-gradient(to top, color-mix(in srgb, var(--seedz-ink) 88%, transparent) 0px, color-mix(in srgb, var(--seedz-ink) 88%, transparent) 96px, transparent)',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />

      {/* Logo lockup — top-left, above the video (z 1) + scrim (z 10)
          layers. */}
      <div style={{ position: 'relative', zIndex: 11 }} className="reveal-1">
        <BrandMark glyphSize={30} />
      </div>

      {/* Footer — bottom-left. --text-muted, not --text-subtle: #75757c
          tops out at 4.03:1 even over solid ink, so the quiet tier
          mathematically cannot clear WCAG here; the mid tier stays quiet
          against the held scrim while actually passing. */}
      <div style={{ position: 'relative', zIndex: 11 }} className="reveal-5">
        <p
          style={{
            margin: '0 0 5px',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-2xs)',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Secure · Compliant · Reliable
        </p>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-2xs)',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
          }}
        >
          © 2026 Seedz Technology Group
        </p>
      </div>
    </div>
  );
}
